import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { playableUrl } from "@/lib/creatorApi";
import { fmtClock } from "@/lib/studioApi";

const PLAY_EVENT = "elpm-version-play";

/** Before/After switch: flips between your upload and this version, carrying on from the same spot. */
function SideSwitch({ id, side, alt, onFlip }) {
  const btn = (key, label) => (
    <button type="button" data-testid={`version-ab-${key}-${id}`} aria-pressed={side === key} onClick={() => onFlip(key)}
      className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors duration-200 ${side === key ? "bg-white text-[#05070f]" : "text-white/60 hover:text-white"}`}>
      {label}
    </button>
  );
  return (
    <div data-testid={`version-ab-${id}`} className="mb-3 inline-flex rounded-full border border-white/10 bg-black/40 p-0.5">
      {btn("before", alt.beforeLabel)}{btn("after", alt.afterLabel)}
    </div>
  );
}

/** Compact player for one version; starting one pauses every other. `alt` adds the Before/After switch. */
export default function VersionPlayer({ id, url, duration, alt }) {
  const audioRef = useRef(null);
  const resume = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [total, setTotal] = useState(duration || 0);
  const [side, setSide] = useState("after");
  const src = side === "before" && alt ? alt.url : playableUrl(url);

  useEffect(() => {
    const onOther = (e) => { if (e.detail !== id) audioRef.current?.pause(); };
    window.addEventListener(PLAY_EVENT, onOther);
    return () => window.removeEventListener(PLAY_EVENT, onOther);
  }, [id]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: id }));
      a.play().catch(() => setPlaying(false));
    } else a.pause();
  };
  const flip = (next) => {
    const a = audioRef.current;
    if (next === side) return;
    resume.current = a ? { t: a.currentTime, play: !a.paused } : null;
    setSide(next);
  };
  const loaded = (e) => {
    const a = e.currentTarget;
    if (Number.isFinite(a.duration)) setTotal(a.duration);
    const r = resume.current;
    resume.current = null;
    if (!r) return;
    const end = Number.isFinite(a.duration) ? a.duration : Infinity;
    a.currentTime = r.t < end - 0.5 ? r.t : 0;
    if (r.play) a.play().catch(() => setPlaying(false));
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !total) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * total;
  };
  const pct = total ? Math.min(100, (time / total) * 100) : 0;

  return (
    <div>
      {alt ? <SideSwitch id={id} side={side} alt={alt} onFlip={flip} /> : null}
      <div data-testid={`version-player-${id}`} data-side={side} className="flex items-center gap-3">
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={loaded}
        />
        <button type="button" data-testid={`version-play-${id}`} onClick={toggle} aria-label={playing ? "Pause" : "Play"} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-[#05070f] transition-transform duration-200 hover:scale-105">
          {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div role="slider" tabIndex={0} aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(total)} aria-valuenow={Math.round(time)} data-testid={`version-seek-${id}`} onClick={seek} className="group relative h-2 cursor-pointer rounded-full bg-white/10">
            <div className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${side === "before" ? "from-emerald-300 to-teal-500" : "from-blue-400 to-violet-500"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 flex justify-between font-mono text-[10px] text-white/45">
            <span data-testid={`version-time-${id}`}>{fmtClock(time)}</span><span>{fmtClock(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
